import { SiteWithStacks } from '@pubstack/common/src/site'
import { FunctionComponent, useState } from 'react'
import { useUpdateSite } from '~/api/app/sites.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { ConfirmationPhraseField, isPhraseTyped } from '~/components/ConfirmationPhraseField'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Modal } from '~/components/Modal'
import { SITE_PRODUCT_LABEL, siteProductOf } from './siteProduct'

type Props = {
  media: SiteWithStacks
}

/**
 * The confirmation for archiving a media, from the media list.
 *
 * Archiving is stored as `enabled: false`, and the server turns that into more than a hidden row: it
 * archives the media's stacks and drops its bid params mappings. So the copy names the consequence the
 * viewer is actually buying, and names it differently per product — an Analytics media only stops
 * reporting, while a One or Node media also stops serving.
 *
 * It is reversible, but not by the person clicking: unarchiving is Pubstack-only, and saying so here is
 * what stops "I'll just put it back" being discovered as a 403 later.
 *
 * Which is also why the name has to be typed back before Archive opens: the action is reached from a row
 * in a list, where the cheapest mistake is archiving the media next to the one meant, and typing the name
 * is what makes the viewer read which row they are on. Same gate as the one-way saves in
 * `SiteSaveConfirmationModal`.
 */
export const ArchiveMediaModal: FunctionComponent<Props> = ({ media }) => {
  const user = useAuthenticatedUser()
  const { close } = useGlobalModal()
  const product = siteProductOf(media)
  const servesAds = product !== 'analytics'
  const [typedName, setTypedName] = useState('')
  const isNameConfirmed = isPhraseTyped(media.name, typedName)

  const { mutateAsync: updateSite, isPending } = useUpdateSite(user.currentScopeId, media.id, {
    successToast: (
      <>
        <strong>{media.name}</strong> was archived.
      </>
    ),
    errorToast: (
      <>
        An error occurred while trying to archive <strong>{media.name}</strong>.
      </>
    ),
  })

  const handleArchive = async () => {
    // The disabled button is the affordance; not archiving is the invariant.
    if (isPending || !isNameConfirmed) {
      return
    }
    // Without its stacks: the list joins them for display, and the update endpoint has no use for them.
    const { stacks: _stacks, ...site } = media
    await updateSite({ ...site, enabled: false })
    close()
  }

  return (
    <Modal.Content>
      <Modal.Title>Archive media</Modal.Title>
      <Modal.Body>
        <div className={'flex flex-col gap-3'}>
          <p>
            You are about to archive <strong>{media.name}</strong>, a {SITE_PRODUCT_LABEL[product]} media.
          </p>
          <p>
            {servesAds
              ? 'It will leave the media list, its stacks will be archived and stop serving, and its bid params mappings will be dropped. Its data will no longer be visible in Pubstack.'
              : 'It will leave the media list, and its data will no longer be visible in Pubstack.'}
          </p>
          <p>Archived medias stay on a separate page, but only Pubstack can bring one back.</p>
          <ConfirmationPhraseField phrase={media.name} value={typedName} onChange={setTypedName} disabled={isPending} trackingLabel={'common-medias-archive-type'} />
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'common-medias-archive-cancel'}>
          Cancel
        </Button>
        <Button
          variant={'negative'}
          iconName={isPending ? 'pubstack' : 'archive'}
          iconSpin={isPending}
          disabled={isPending || !isNameConfirmed}
          onClick={handleArchive}
          trackingLabel={'common-medias-archive-confirm'}
        >
          {isPending ? 'Archiving media...' : 'Archive media'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
